import { DetectedLangBanner } from "@/components/DetectedLangBanner";
import DraftResumeBanner from "@/components/DraftResumeBanner";
import PendingCheckoutBanner from "@/components/PendingCheckoutBanner";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { B2CAuthProvider } from "@/context/B2CAuthContext";
import IdleLogoutGuard from "@/components/IdleLogoutGuard";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import GoogleAuthWatcher from "@/components/GoogleAuthWatcher";
import PublicHeader from "@/components/PublicHeader";
import FadeOutlet from "@/components/FadeOutlet";
import SiteFooter from "@/components/SiteFooter";
import CookieBanner from "@/components/CookieBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PreviewRegionBanner from "@/components/PreviewRegionBanner";
import BackButton from "@/components/BackButton";
import ScrollToTop from "@/components/ScrollToTop";

import ImpersonationApprove from "@/pages/public/ImpersonationApprove";
import DashboardLayout from "@/components/DashboardLayout";
import SuperadminLayout from "@/components/SuperadminLayout";
import QuoteView from "@/pages/public/QuoteView";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import Reactivate from "@/pages/auth/Reactivate";
import ActivateMember from "@/pages/auth/ActivateMember";

import Registry from "@/pages/b2b/Registry";
import Storage from "@/pages/b2b/Storage";
import Deposit from "@/pages/b2b/Deposit";
import Declarations from "@/pages/b2b/Declarations";
import Stats from "@/pages/b2b/Stats";
import Team from "@/pages/b2b/Team";
import Account from "@/pages/b2b/Account";
import Reviews from "@/pages/b2b/Reviews";
import Shipments from "@/pages/b2b/Shipments";
import Restitutions from "@/pages/b2b/Restitutions";
import Invoices from "@/pages/b2b/Invoices";
import MatchHistory from "@/pages/b2b/MatchHistory";
import Establishments from "@/pages/b2b/Establishments";
import Settings from "@/pages/b2b/Settings";
import Rewards from "@/pages/b2b/Rewards";
import { OwnerAdminRoute } from "@/components/OwnerAdminRoute";
import Superadmin from "@/pages/Superadmin";
import SuperadminLogin from "@/pages/superadmin/SuperadminLogin";
import SuperadminForgot from "@/pages/superadmin/SuperadminForgot";

import Partners from "@/pages/public/Partners";
import PartenaireDetail from "@/pages/public/PartenaireDetail";
import Declare from "@/pages/public/Declare";
import LibreService from "@/pages/public/LibreService";
import Track from "@/pages/public/Track";
import Pay from "@/pages/public/Pay";
import Verifier from "@/pages/public/Verifier";
import PaystackCallback from "@/pages/public/PaystackCallback";
import NotFound from "@/pages/public/NotFound";
import Ship from "@/pages/public/Ship";
import Annonces from "@/pages/public/Annonces";
import FinderTrack from "@/pages/public/FinderTrack";
import RenewContract from "@/pages/public/RenewContract";
import AnnonceDetail from "@/pages/public/AnnonceDetail";
import DeclareLost from "@/pages/public/DeclareLost";
import DeclareChoice from "@/pages/public/DeclareChoice";
import DeclareFound from "@/pages/public/DeclareFound";
import Legal from "@/pages/public/Legal";
import Contact from "@/pages/public/Contact";
import Carte from "@/pages/public/Carte";
import Classement from "@/pages/public/Classement";
import TrouveurProfile from "@/pages/public/TrouveurProfile";
import Retro from "@/pages/public/Retro";
import PressKit from "@/pages/public/PressKit";
import PaymentResult from "@/pages/public/PaymentResult";
import Rate from "@/pages/public/Rate";
import AvisFound from "@/pages/public/AvisFound";
import Join from "@/pages/public/Join";
import MonCompte from "@/pages/b2c/MonCompte";
import ObjetTrouve from "@/pages/b2c/ObjetTrouve";
import ValiderReclamation from "@/pages/public/ValiderReclamation";
import ReclamationSecours from "@/pages/public/ReclamationSecours";

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <div className="flex-1">
        <FadeOutlet />
      </div>
      <SiteFooter />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <LanguageProvider>
        <AuthProvider>
        <B2CAuthProvider>
          <GoogleAuthWatcher />
          <DraftResumeBanner />
          <PendingCheckoutBanner />
          <IdleLogoutGuard />
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <CookieBanner />
          <ImpersonationBanner />
          <DetectedLangBanner />
          <PreviewRegionBanner />
          <BackButton />
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partenaires/:partnerId" element={<PartenaireDetail />} />
              <Route path="/declare/:partnerId" element={<Declare />} />
              <Route path="/declarer" element={<DeclareChoice />} />
              <Route path="/objets/:partnerId" element={<LibreService />} />
              <Route path="/join/:code" element={<Join />} />
              <Route path="/track" element={<Track />} />
              <Route path="/track/:declarationId" element={<Track />} />
              <Route path="/verifier/:declarationId" element={<Verifier />} />
              <Route path="/pay/:declarationId" element={<Pay />} />
              <Route path="/ship/:declarationId" element={<Ship />} />
              <Route path="/rate/:declarationId" element={<Rate />} />
              <Route path="/avis/:foundId" element={<AvisFound />} />
              <Route path="/renew/:token" element={<RenewContract />} />
              <Route path="/mon-compte" element={<MonCompte />} />
              <Route path="/objet-trouve/:itemId" element={<ObjetTrouve />} />
              <Route path="/valider-reclamation/:claimId" element={<ValiderReclamation />} />
              <Route path="/reclamation-secours/:token" element={<ReclamationSecours />} />
              <Route path="/suivi-trouve/:adId" element={<FinderTrack />} />
              <Route path="/annonces" element={<Annonces />} />
              <Route path="/objets-trouves" element={<Annonces adType="found" />} />
              <Route path="/annonces/nouvelle" element={<DeclareLost />} />
              <Route path="/annonces/trouvee" element={<DeclareFound />} />
              <Route path="/annonces/:adId" element={<AnnonceDetail />} />
              <Route path="/carte" element={<Carte />} />
              <Route path="/classement" element={<Classement />} />
              <Route path="/trouveur/:id" element={<TrouveurProfile />} />
              <Route path="/cgu" element={<Legal doc="terms" />} />
              <Route path="/confidentialite" element={<Legal doc="privacy" />} />
              <Route path="/mentions-legales" element={<Legal doc="mentions" />} />
              <Route path="/cookies" element={<Legal doc="cookies" />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/presse" element={<PressKit />} />
            </Route>

            {/* Standalone (no header/footer) */}
            <Route path="/retro" element={<Retro />} />
            <Route path="/devis/:token" element={<QuoteView />} />
            <Route path="/impersonation/approve/:token" element={<ImpersonationApprove />} />
            <Route path="/payment/success" element={<PaymentResult />} />
            <Route path="/payment/paystack-callback" element={<PaystackCallback />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/register" element={<Register />} />
            <Route path="/auth/forgot" element={<Forgot />} />
            <Route path="/auth/reset" element={<Reset />} />
            <Route path="/auth/reactivate" element={<Reactivate />} />
            <Route path="/team/activate" element={<ActivateMember />} />

            {/* B2B dashboard */}
            <Route path="/b2b" element={<DashboardLayout />}>
              <Route path="establishments" element={<Establishments />} />
              <Route path="registry" element={<Registry />} />
              <Route path="storage" element={<Storage />} />
              <Route path="deposit" element={<Deposit />} />
              <Route path="declarations" element={<Declarations />} />
              <Route path="match-history" element={<MatchHistory />} />
              <Route path="stats" element={<Stats />} />
              <Route path="settings" element={<Settings />} />
              <Route path="team" element={<OwnerAdminRoute><Team /></OwnerAdminRoute>} />
              <Route path="account" element={<OwnerAdminRoute><Account /></OwnerAdminRoute>} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="shipments" element={<Shipments />} />
              <Route path="restitutions" element={<Restitutions />} />
              <Route path="invoices" element={<OwnerAdminRoute><Invoices /></OwnerAdminRoute>} />
            </Route>
            <Route path="/superadmin/login" element={<SuperadminLogin />} />
            <Route path="/superadmin/forgot" element={<SuperadminForgot />} />
            <Route path="/superadmin" element={<SuperadminLayout />}>
              <Route index element={<Superadmin />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </B2CAuthProvider>
        </AuthProvider>
        </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
